import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import CSSModules from "lib/utils/css-modules";
import { ResponsiveContainer, AreaChart, XAxis, YAxis, Tooltip, Area } from "recharts";
import { mapValues, groupBy } from "lodash";

import { st, MainView, SubBar, Link, Button, OrdersList, HintView, Message } from "lib/index";
import { stateSelector, initializeView, fetchCounters, fetchPendingOrders } from "./index";
import DashboardHint from "./DashboardHint";
import styles from "./DashboardView.less";

const DashboardCounters = CSSModules(styles)(({ counters }) => {
  return (
    <div styleName="numbers">
      <div styleName="number">
        <div styleName="number-digit">{counters.orders_count}</div>
        <div styleName="number-label">{I18n.t("papp.dashboard.DashboardView.orders_number_label")}</div>
      </div>
      <div styleName="number">
        <div styleName="number-digit">{counters.persons_count}</div>
        <div styleName="number-label">{I18n.t("papp.dashboard.DashboardView.participants_number_label")}</div>
      </div>
      <div styleName="number">
        <div styleName="number-digit">{counters.finished_orders_count}</div>
        <div styleName="number-label">{I18n.t("papp.dashboard.DashboardView.finished_orders_number_label")}</div>
      </div>
      {!!counters.finished_orders_total_amount_sum && (
        <div styleName="number">
          <div styleName="number-digit">{st.formatMoney(counters.finished_orders_total_amount_sum)}</div>
          <div styleName="number-label">
            {I18n.t("papp.dashboard.DashboardView.finished_orders_total_amount_label")}
          </div>
        </div>
      )}
    </div>
  );
});

const DashboardGraph = CSSModules(styles)(({ data }) => {
  data = data.map(([date, count]) => ({ date: moment(date).format("D MMM"), count }));
  return (
    <div styleName="graph">
      <div styleName="graph-inner">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Area
              type="linear"
              dataKey="count"
              name={I18n.t("papp.dashboard.DashboardView.created_orders_number_label")}
              stroke="#38aa82"
              fill="#38aa82"
              fillOpacity={0.3}
              dot={{ fillOpacity: 0.8, r: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

@connect(
  (state) => ({
    currentCompanyId: state.auth.company.model.id,
    counters: stateSelector(state).counters.model,
    pendingOrders: stateSelector(state).pendingOrders.model && stateSelector(state).pendingOrders.model.records,
    pendingOrdersTotalCount:
      stateSelector(state).pendingOrders.model && stateSelector(state).pendingOrders.model.meta.total_count,
    fetchDone: stateSelector(state).counters.fetchDone && stateSelector(state).pendingOrders.fetchDone,
    fetchLoading: stateSelector(state).counters.fetchLoading || stateSelector(state).pendingOrders.fetchLoading,
    fetchFailed: stateSelector(state).counters.fetchFailed || stateSelector(state).pendingOrders.fetchFailed,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        initializeView,
        fetch: () => (dispatch) => {
          dispatch(fetchCounters());
          dispatch(fetchPendingOrders());
        },
      },
      dispatch
    )
)
@CSSModules(styles)
export default class DashboardView extends React.PureComponent {
  componentDidMount() {
    this.props.initializeView();
  }

  render() {
    const {
      currentCompanyId,
      counters,
      pendingOrders,
      pendingOrdersTotalCount,
      fetchDone,
      fetchLoading,
      fetchFailed,
      fetch,
    } = this.props;

    return (
      <MainView.Container>
        <SubBar>
          <h1>{I18n.t("papp.dashboard.DashboardView.title")}</h1>
        </SubBar>

        <MainView.Content hasSubBar>
          <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={fetch} />

          <div styleName="container">
            <div styleName="column-left">
              {fetchDone && (
                <div styleName="section-orders">
                  <div styleName="section-title">
                    <h3>{I18n.t("papp.dashboard.DashboardView.orders_waiting_for_your_approval_title")}</h3>
                    {pendingOrdersTotalCount > 3 && (
                      <Button
                        as={Link}
                        floated="right"
                        size="small"
                        to={`/c/${currentCompanyId}/orders?status=waiting_for_company_approval`}
                        content={I18n.t("papp.dashboard.DashboardView.see_all_orders", {
                          count: pendingOrdersTotalCount,
                        })}
                      />
                    )}
                  </div>
                  <div styleName="section-content">
                    {pendingOrders.length > 0 && <OrdersList orders={pendingOrders} showStatus={false} />}

                    {pendingOrders.length === 0 && (
                      <p>{I18n.t("papp.dashboard.DashboardView.no_orders_waiting_message")}</p>
                    )}
                  </div>
                </div>
              )}

              {fetchDone && (
                <div styleName="section-graph">
                  <div styleName="section-title">
                    <h3>
                      {I18n.t("papp.dashboard.DashboardView.new_orders_title", {
                        date_from: st.formatDate(counters.date_from, { format: "DD MMM" }),
                        date_to: st.formatDate(counters.date_to, { format: "DD MMM" }),
                      })}
                    </h3>
                  </div>
                  {counters.graph_data.length > 0 && <DashboardGraph data={counters.graph_data} />}
                  {counters.graph_data.length === 0 && (
                    <p>{I18n.t("papp.dashboard.DashboardView.no_graph_data_message")}</p>
                  )}
                </div>
              )}
            </div>

            <div styleName="column-right">
              <div styleName="section">
                <DashboardHint />
              </div>

              {fetchDone && (
                <div styleName="section-numbers">
                  <div styleName="section-title">
                    <h3>
                      {I18n.t("papp.dashboard.DashboardView.stats_title", {
                        date_from: st.formatDate(counters.date_from, { format: "DD MMM" }),
                        date_to: st.formatDate(counters.date_to, { format: "DD MMM" }),
                      })}
                    </h3>
                  </div>
                  <DashboardCounters counters={counters} />
                </div>
              )}
            </div>
          </div>
        </MainView.Content>
      </MainView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/dashboard/DashboardView.jsx